import { UserProfile } from '@cpd-app/core-module/models';
import { Action, createReducer, on } from '@ngrx/store';

import * as UserProfileActions from './user-profile.actions';
import * as UserProfileFeatureState from './user-profile.state';

/**
 * The booleans a save response is allowed to RAISE but never to LOWER.
 *
 * Every profile the subgraph hands back is built by
 * `getUserProfileDataContractByUserId`, and that query selects these as
 * literals before anything fills them in:
 *
 *   false AS "isSkillsSurveyComplete"
 *   false AS "isInterestsSurveyComplete"
 *   false AS "isWorkValuesSurveyComplete"
 *   false AS "isLifestyleSurveyComplete"
 *   false AS "isJstUploaded"
 *   false AS "hasReturnUrl"
 *   false AS "userHasPreGoal"
 *
 * Four of them are then set to true by a row-existence union, and
 * `isSkillsSurveyComplete` is RE-DERIVED by `applyBusinessRules` against a
 * rule stricter than the survey itself (military needs a branch AND ranks AND
 * job codes AND education, all present in the same response). The remaining
 * two, `hasReturnUrl` and `userHasPreGoal`, are never populated at all: the
 * only thing that has ever set them true is this reducer.
 *
 * So a false on any of these is not a measurement, it is the absence of one,
 * and a reconciliation that let it through would revoke access the member
 * already has. `isSkillsSurveyComplete` gates the occupation card locks, the
 * goals report guard, the Via button and the connect sidebar, so lowering it
 * is a worse outcome than the staleness this reconciliation exists to fix.
 *
 * `hasCivilianWorkExperience` is deliberately NOT here. It is a real answer
 * the member can change, it feeds the completion derivation above, and the
 * server reports it from actual work-experience rows.
 */
type CompletionLatch =
  | 'isSkillsSurveyComplete'
  | 'isInterestsSurveyComplete'
  | 'isWorkValuesSurveyComplete'
  | 'isLifestyleSurveyComplete'
  | 'isJstUploaded'
  | 'hasReturnUrl'
  | 'userHasPreGoal';

const COMPLETION_LATCHES: CompletionLatch[] = [
  'isSkillsSurveyComplete',
  'isInterestsSurveyComplete',
  'isWorkValuesSurveyComplete',
  'isLifestyleSurveyComplete',
  'isJstUploaded',
  'hasReturnUrl',
  'userHasPreGoal'
];

/**
 * Take the server's answer, keep the latches it cannot report.
 *
 * The server's profile wins on everything else, which is the entire point:
 * the per-step save strips sections out of its payload on purpose so the
 * backend leaves them alone, and until this ran the store kept the stripped
 * copy. The response carries the real job codes, ranks and education rows
 * back, which is what stops each step from building on the last step's
 * hollowed-out clone.
 */
function reconcileWithServerProfile(
  current: UserProfile,
  fromServer: UserProfile
): UserProfile {
  /*
   * Object.create plus Object.assign, not a spread.
   *
   * `UserProfile` is a class and `mpcCodeList` is a getter on it, which the
   * military stepper compares before and after a rank change to decide
   * whether the job codes it collected are still valid. `{ ...profile }`
   * returns a plain object, the getter leaves with the prototype, and the
   * comparison then reads undefined on both sides and silently stops
   * clearing. The rest of this file predates that discovery and spreads
   * freely; new code here does not.
   */
  const reconciled: UserProfile = Object.assign(
    Object.create(Object.getPrototypeOf(fromServer)),
    fromServer
  );

  if (!current) {
    return reconciled;
  }

  for (const latch of COMPLETION_LATCHES) {
    if (current[latch] && !reconciled[latch]) {
      reconciled[latch] = true;
    }
  }

  return reconciled;
}

const userProfileReducer =
  createReducer<UserProfileFeatureState.UserProfileState>(
    UserProfileFeatureState.initialUserProfileState,

    on(UserProfileActions.saveUserProfileToState, (state, action) => ({
      ...state,
      userProfile: action.userProfile,
      isUserProfileSaved: true
    })),

    on(
      UserProfileActions.userSkillsSurveySavedSuccess,
      UserProfileActions.userInterestsSurveySavedSuccess,
      UserProfileActions.userWorkValuesSurveySavedSuccess,
      UserProfileActions.userLifestyleSurveySavedSuccess,
      UserProfileActions.updateUserProfileMilitaryTransitionSuccess,
      UserProfileActions.updateUserProfileWorkTransitionSuccess,

      UserProfileActions.userProfileLoadedSuccess,

      /*
       * THESE TWO ARE REQUEST ACTIONS, AND THAT IS ON PURPOSE.
       *
       * Everything above is a server answer. These two are the optimistic
       * write, and it is load-bearing rather than cosmetic, so it stays:
       *
       *  - `MilitarySkillsContainerComponent.onMilitaryServeTypeSelection`
       *    dispatches and then reads `this.isShowLocationStep` on the very
       *    next line. That getter reads `this.userProfile`, which is fed by
       *    the store subscription, so without a synchronous write the duty
       *    station step is decided from the PREVIOUS serve type and the
       *    member is never asked for their installation.
       *  - `onBranchSelection` and `onRankSelection` compare the incoming
       *    answer against `this.userProfile` to decide whether a branch or
       *    rank change invalidated the ranks and job codes underneath it.
       *  - `MilitarySkillsProfileTypeComponent` clears
       *    `isSkillsSurveyComplete` when the member switches between the
       *    military and civilian tracks, and that reset has to be visible
       *    before the round trip finishes.
       *
       * `updateUserProfileFromOtherSkills` has no effect behind it at all, so
       * for that one this handler is not an optimistic write, it is the only
       * write there is.
       *
       * What was missing was the other half: nothing reconciled the store
       * with what the database actually stored, because
       * `updateUserProfileFromMilitarySkillsSuccess` was handled nowhere. See
       * the handler immediately below.
       */
      UserProfileActions.updateUserProfileFromMilitarySkills,
      UserProfileActions.updateUserProfileFromOtherSkills,
      (state, { userProfile }) => {
        // Never let a sparse/absent payload replace a loaded profile. Use
        // deleteUserProfile to clear it deliberately.
        if (!userProfile) {
          return state;
        }
        return {
          ...state,
          userProfile: userProfile,
          isUserProfileSaved: true
        };
      }
    ),

    /*
     * THE PER-STEP SAVE'S ANSWER, WHICH USED TO GO NOWHERE.
     *
     * `updateUserProfileFromMilitarySkillsSuccess` is dispatched by
     * `updateUserProfileFromMilitarySkills$` with the profile the database
     * holds after the write, and this reducer handled it zero times. Only the
     * REQUEST action was listed above, so the store kept whatever the
     * component optimistically built and was never told what was really
     * stored.
     *
     * That is what compounds. The per-step save deliberately deletes the
     * sections it did not author from its payload, because the backend treats
     * an empty list as "leave this alone" for job codes, ranks and education.
     * The optimistic write then put that hollowed-out copy in the store, the
     * next step cloned the store to build ITS payload, and so each step
     * carried the previous step's holes forward. Every field the survey
     * happened to strip stuck, which is the mechanism behind the "my answers
     * were erased" reports.
     *
     * Last write wins, deliberately: the optimistic write still lands first
     * so the stepper keeps its immediate feedback, and this corrects it once
     * the server answers. The one thing the server's answer is not allowed to
     * do is lower a completion latch. See COMPLETION_LATCHES above.
     */
    on(
      UserProfileActions.updateUserProfileFromMilitarySkillsSuccess,
      (state, { userProfile }) => {
        // Same rule as the handler above: a sparse or absent payload must
        // never replace a loaded profile.
        if (!userProfile) {
          return state;
        }
        return {
          ...state,
          userProfile: reconcileWithServerProfile(
            state.userProfile,
            userProfile
          ),
          isUserProfileSaved: true
        };
      }
    ),

    on(
      UserProfileActions.userShareProfileLoadedSuccess,
      (state, { userProfile }) => ({
        ...state,
        userShareProfile: userProfile
      })
    ),

    on(
      UserProfileActions.saveMilitaryServeTypeSuccess,
      (state, { serveTypeId }) => ({
        ...state,
        userProfile: {
          ...state.userProfile,
          serveTypeId
        } as UserProfile
      })
    ),

    on(
      UserProfileActions.saveMilitaryAirbaseLocationSuccess,
      (state, { userAirbaseLocation }) => ({
        ...state,
        userProfile: {
          ...state.userProfile,
          userAirbaseLocation
        } as UserProfile
      })
    ),

    on(
      UserProfileActions.saveMilitarySpacebaseLocationSuccess,
      (state, { userSpacebaseLocation }) => ({
        ...state,
        userProfile: {
          ...state.userProfile,
          userSpacebaseLocation
        } as UserProfile
      })
    ),

    on(
      UserProfileActions.saveMilitaryInstallationSuccess,
      (state, { userInstallation }) => ({
        ...state,
        userProfile: {
          ...state.userProfile,
          userInstallation
        } as UserProfile
      })
    ),

    /*
     * ASVAB.9. The server's answer to a score-set save, and only its two ASVAB
     * fields. Before this, none of the three places that save scores told the
     * store, so the survey opened with a stale copy of what the home panel had
     * just saved, and saving it would have put the old numbers back.
     */
    on(
      UserProfileActions.asvabScoresSaved,
      (state, { asvabScoreSet, asvabScores }) =>
        state.userProfile
          ? {
              ...state,
              userProfile: {
                ...state.userProfile,
                asvabScoreSet,
                asvabScores
              } as UserProfile
            }
          : state
    ),

    on(UserProfileActions.deleteUserProfile, (state) => {
      return {
        ...state,
        userProfile: null,
        isUserProfileSaved: false
      };
    }),

    on(UserProfileActions.invalidateUserProfile, (state) => ({
      ...state,
      isUserProfileSaved: false
    })),

    on(UserProfileActions.userProfileHasReturnLink, (state) => {
      const updatedUserProfile = {
        ...state.userProfile,
        hasReturnUrl: true,
        returnUrlLastSavedDate: new Date()
      } as UserProfile;
      return { ...state, userProfile: updatedUserProfile };
    }),

    on(UserProfileActions.saveUserPreGoalsSuccess, (state) => ({
      ...state,
      userProfile: {
        ...state.userProfile,
        userHasPreGoal: true
      } as UserProfile
    })),

    on(UserProfileActions.acceptPrivacyPolicySuccess, (state) => ({
      ...state,
      userProfile: {
        ...state.userProfile,
        privacyPolicyAccepted: true
      } as UserProfile
    })),
    on(
      UserProfileActions.updateProfileWorkTypeSuccess,
      (state, { profileWorkType }) => ({
        ...state,
        userProfile: {
          ...state.userProfile,
          profileWorkType
        } as UserProfile
      })
    ),
    on(UserProfileActions.acceptBannerPrivacyPolicy, (state) => ({
      ...state,
      userProfile: {
        ...state.userProfile,
        profileWorkType: {
          type: state.userProfile.profileWorkType?.type,
          servicepolicyaccepted: true
        }
      } as UserProfile
    }))
  );

export function reducer(
  state: UserProfileFeatureState.UserProfileState | undefined,
  action: Action
) {
  return userProfileReducer(state, action);
}
